/**
 * Unit Routes
 *
 * Route definitions for the units module.
 */

import { lazy } from 'react';
import { Navigate, useParams } from 'react-router-dom';

function UnitFilesDocumentClassifierRedirect() {
    const { id } = useParams<{ id: string }>();
    return <Navigate to={`/units/${id}/files/data-rooms`} replace />;
}

const UnitManagement = lazy(() => import('@/pages/units/UnitManagement'));
const UnitDetailLayout = lazy(() => import('@/pages/units/UnitDetailLayout'));
const UnitOverviewPage = lazy(() => import('@/pages/units/UnitOverviewPage'));
const UnitTenanciesPage = lazy(() => import('@/pages/units/UnitTenanciesPage'));
const UnitContractsPage = lazy(() => import('@/pages/units/UnitContractsPage'));
const UnitDefectsPage = lazy(() => import('@/pages/units/UnitDefectsPage'));
const UnitDefectDetailPage = lazy(() => import('@/pages/units/UnitDefectDetailPage'));
const UnitOwnersPage = lazy(() => import('@/pages/units/UnitOwnersPage'));
const UnitHoaFeePage = lazy(() => import('@/pages/units/UnitHoaFeePage'));
const UnitTransactionsPage = lazy(() => import('@/pages/units/UnitTransactionsPage'));
const UnitFilesLayout = lazy(() => import('@/pages/units/UnitFilesLayout'));
const UnitFilesDataRoomsPage = lazy(() => import('@/pages/units/UnitFilesDataRoomsPage'));
const UnitMediaPage = lazy(() => import('@/pages/units/UnitMediaPage'));
const UnitNotesPage = lazy(() => import('@/pages/units/UnitNotesPage'));
const UnitCalendarPage = lazy(() => import('@/pages/units/UnitCalendarPage'));
const UnitTasksPage = lazy(() => import('@/pages/units/UnitTasksPage'));
const UnitRenovationOverviewPage = lazy(() => import('@/pages/units/UnitRenovationOverviewPage'));
const UnitRenovationFloorPlanPage = lazy(() => import('@/pages/units/UnitRenovationFloorPlanPage'));
const UnitElementsPage = lazy(() => import('@/pages/units/UnitElementsPage'));
const UnitConstructionPlansPage = lazy(() => import('@/pages/units/UnitConstructionPlansPage'));
const UnitConstructionPlanDetailPage = lazy(() => import('@/pages/units/UnitConstructionPlanDetailPage'));
const UnitProgressPlanPage = lazy(() => import('@/pages/units/UnitProgressPlanPage'));
const UnitInspectionsPage = lazy(() => import('@/pages/units/UnitInspectionsPage'));
const UnitInspectionDetailPage = lazy(() => import('@/pages/units/UnitInspectionDetailPage'));
const UnitInspectionCraftsPage = lazy(() => import('@/pages/units/UnitInspectionCraftsPage'));
const UnitInspectionCraftDetailPage = lazy(() => import('@/pages/units/UnitInspectionCraftDetailPage'));
const UnitRentingOverviewPage = lazy(() => import('@/pages/units/UnitRentingOverviewPage'));
const UnitKeysMetersPage = lazy(() => import('@/pages/units/UnitKeysMetersPage'));
const UnitContactsPage = lazy(() => import('@/pages/units/UnitContactsPage'));
const UnitRentableUnitDetailPage = lazy(() => import('@/pages/units/UnitRentableUnitDetailPage'));
const RentableUnitsListRedirect = lazy(() => import('@/pages/units/RentableUnitsListRedirect'));
const UnitApprovalsLayout = lazy(() => import('@/pages/units/UnitApprovalsLayout'));
const UnitEnvelopesPage = lazy(() => import('@/pages/units/UnitEnvelopesPage'));
const UnitEnvelopeCreatePage = lazy(() => import('@/pages/units/UnitEnvelopeCreatePage'));
const UnitEnvelopeDetailPage = lazy(() => import('@/pages/units/UnitEnvelopeDetailPage'));
const UnitEnvelopeAdditionalDocumentPage = lazy(() => import('@/pages/units/UnitEnvelopeAdditionalDocumentPage'));

export const unitRoutes = [
    {
        path: 'units',
        element: <UnitManagement />,
    },
    {
        path: 'units/:id',
        element: <UnitDetailLayout />,
        children: [
            { index: true, element: <Navigate to="overview" replace /> },
            { path: 'overview', element: <UnitOverviewPage /> },
            { path: 'tasks', element: <UnitTasksPage /> },
            { path: 'tenancies', element: <UnitTenanciesPage /> },
            { path: 'contracts', element: <UnitContractsPage /> },
            { path: 'defects', element: <UnitDefectsPage /> },
            { path: 'defects/:defectId', element: <UnitDefectDetailPage /> },
            { path: 'owners', element: <UnitOwnersPage /> },
            { path: 'hoafee', element: <UnitHoaFeePage /> },
            { path: 'transactions', element: <UnitTransactionsPage /> },
            {
                path: 'approvals',
                element: <UnitApprovalsLayout />,
                children: [
                    { index: true, element: <UnitEnvelopesPage /> },
                    { path: 'create', element: <UnitEnvelopeCreatePage /> },
                    { path: ':envelopeId', element: <UnitEnvelopeDetailPage /> },
                    { path: ':envelopeId/additional-document', element: <UnitEnvelopeAdditionalDocumentPage /> },
                ],
            },
            {
                path: 'files',
                element: <UnitFilesLayout />,
                children: [
                    { index: true, element: <Navigate to="data-rooms" replace /> },
                    { path: 'data-rooms', element: <UnitFilesDataRoomsPage /> },
                    { path: 'media', element: <UnitMediaPage /> },
                    { path: 'document-classifier', element: <UnitFilesDocumentClassifierRedirect /> },
                ],
            },
            { path: 'documents', element: <Navigate to="files/data-rooms" replace /> },
            { path: 'media', element: <Navigate to="files/media" replace /> },
            { path: 'notes', element: <UnitNotesPage /> },
            { path: 'calendar', element: <UnitCalendarPage /> },
            { path: 'floor-plan', element: <UnitRenovationFloorPlanPage /> },
            { path: 'elements', element: <UnitElementsPage /> },
            { path: 'construction-plans', element: <UnitConstructionPlansPage /> },
            { path: 'construction-plans/:planId', element: <UnitConstructionPlanDetailPage /> },
            { path: 'inspections', element: <UnitInspectionsPage /> },
            { path: 'inspections/:inspectionId', element: <UnitInspectionDetailPage /> },
            { path: 'inspections/:inspectionId/crafts', element: <UnitInspectionCraftsPage /> },
            { path: 'inspections/:inspectionId/crafts/:constructionCraft', element: <UnitInspectionCraftDetailPage /> },
            { path: 'progress-plan', element: <UnitProgressPlanPage /> },
            { path: 'renovation', element: <UnitRenovationOverviewPage /> },
            { path: 'renting', element: <UnitRentingOverviewPage /> },
            { path: 'keys-meters', element: <UnitKeysMetersPage /> },
            { path: 'contacts', element: <UnitContactsPage /> },
            { path: 'rentable-units', element: <RentableUnitsListRedirect /> },
            { path: 'rentable-units/:rentableUnitId', element: <UnitRentableUnitDetailPage /> },
        ],
    },
];
